import { useEffect, useRef, useState, type FormEvent } from "react";
import { X, Check, UserPlus } from "lucide-react";
import { Button, Field, EmptyState, Icon, Tag } from "@cntrl/ui";
import { useLiveGroup } from "../../app/realtime/RealtimeProvider.js";
import {
  useGuestList,
  useAddGuest,
  useGuestPartners,
  useRemoveGuest,
  guestCounts,
  guestSeats,
  isPendingGuest,
  type GuestPartnerOption,
} from "../../app/hooks/useGuestList.js";
import { PartnerChips } from "./PartnerChips.js";
import { useCapability } from "../../app/bandContext.js";
import { GuestLoading, GuestQueryError } from "./GuestListStates.js";
import styles from "./GuestListModal.module.scss";
import { partnerRelationshipLabel } from "../../app/partnerRelationship.js";

export interface GuestListModalProps {
  showId: string;
  onClose: () => void;
}

/**
 * Desktop guest-list modal (the routed `/booking/shows/:id/guests` modal). Joins the show:{id}
 * live group so an add anywhere and a scan at the door repaint here within seconds (demo steps 1-2),
 * with the comps figure scan-INDEPENDENT and the arrived counter the live one. Add/remove are
 * optimistic with rollback+toast (useGuestList); every write surfaces the server error verbatim.
 */
export function GuestListModal({ showId, onClose }: GuestListModalProps) {
  useLiveGroup(`show:${showId}`);

  const list = useGuestList(showId);
  const addGuest = useAddGuest(showId);
  const removeGuest = useRemoveGuest(showId);
  // Asked only of a seat that may add guests (the route is guests:write) — a read-only seat gets no chips.
  const partners = useGuestPartners(showId, useCapability("guests:write"));
  const counts = guestCounts(list.data);

  // A chosen partner (ART-239 part 3) fills the name and locks it until it is let go or added.
  const [partner, setPartner] = useState<GuestPartnerOption | null>(null);
  const [name, setName] = useState("");
  const [plusCount, setPlusCount] = useState("0");
  const [note, setNote] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Escape closes the modal (routed-modal rule — URL-addressable, so back/refresh work too).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Focus management for the dialog: move focus into the modal on open, TRAP Tab / Shift+Tab inside
  // it (the background booking screen stays mounted behind the scrim, so without this focus escapes to it),
  // and RESTORE focus to the trigger on close — so a keyboard/AT user isn't dropped onto <body>. The trap is
  // an addEventListener on the modal node (not a JSX handler on a non-interactive element).
  useEffect(() => {
    const el = modalRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    nameRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !el) return;
      const focusables = el.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    el?.addEventListener("keydown", onKey);
    return () => {
      el?.removeEventListener("keydown", onKey);
      previouslyFocused?.focus?.();
    };
  }, []);

  function onAdd(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      nameRef.current?.focus();
      return;
    }
    const plus = Math.max(0, Math.trunc(Number(plusCount) || 0));
    addGuest.mutate(
      { name: trimmed, plusCount: plus, note: note.trim() || null, partnerLinkId: partner?.partnerLinkId ?? null },
      {
        onSuccess: () => {
          setPartner(null);
          setName("");
          setPlusCount("0");
          setNote("");
          nameRef.current?.focus();
        },
      },
    );
  }

  const rows = list.data?.items ?? [];

  return (
    <div className={styles.backdrop}>
      {/* Real <button> scrim so click-outside-to-close is keyboard/AT-reachable (jsx-a11y); Escape + the
          header close button cover the same intent. */}
      <button
        type="button"
        className={styles.scrim}
        aria-label="Close guest list"
        tabIndex={-1}
        onClick={onClose}
      />
      <div
        ref={modalRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="guest-modal-title"
      >
        <header className={styles.header}>
          <div>
            <h2 id="guest-modal-title" className={styles.title}>
              Guest list
            </h2>
            <p className={styles.counts}>
              <span>
                <strong data-testid="comps-count">{counts.comps}</strong> comp
                {counts.comps === 1 ? "" : "s"}
              </span>
              <span aria-hidden="true" className={styles.dot} />
              <span>
                <strong data-testid="arrived-count">{counts.arrived}</strong> arrived
              </span>
            </p>
          </div>
          <button type="button" className={styles.close} aria-label="Close guest list" onClick={onClose}>
            <Icon icon={X} size={20} />
          </button>
        </header>

        {(partners.data?.items?.length ?? 0) > 0 && (
          <div className={styles.partners}>
            <PartnerChips
              options={partners.data}
              selected={partner?.partnerLinkId ?? null}
              onSelect={(o) => {
                setPartner(o);
                setName(o?.guestName ?? "");
              }}
            />
          </div>
        )}
        <form className={styles.addForm} onSubmit={onAdd}>
          <Field
            ref={nameRef}
            label="Guest name"
            placeholder="Name"
            value={name}
            readOnly={partner !== null}
            onChange={(e) => setName(e.target.value)}
            className={styles.nameField}
          />
          <Field
            label="+"
            type="number"
            min={0}
            inputMode="numeric"
            value={plusCount}
            onChange={(e) => setPlusCount(e.target.value)}
            className={styles.plusField}
            aria-label="Plus ones"
          />
          <Field
            label="Note"
            placeholder="Optional"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={styles.noteField}
          />
          <Button
            type="submit"
            className={styles.addBtn}
            loading={addGuest.isPending}
            disabled={!name.trim()}
          >
            <Icon icon={UserPlus} size={16} /> Add
          </Button>
        </form>

        <div className={styles.body}>
          {list.isLoading ? (
            <GuestLoading />
          ) : list.isError ? (
            <GuestQueryError error={list.error} onRetry={() => void list.refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState
              title="No guests yet"
              description="Add names above — they appear here and at the door in real time."
            />
          ) : (
            <ul className={styles.rows}>
              {rows.map((row) => {
                const pending = isPendingGuest(row);
                const scanned = row.scannedOn != null;
                return (
                  <li key={row.guestId} className={styles.row} data-pending={pending || undefined}>
                    <div className={styles.rowMain}>
                      <span className={styles.rowName}>{row.name || "Guest"}</span>
                      {row.source === "partner" && <Tag size="sm">{partnerRelationshipLabel(row.partnerRelationship) ?? "Partner"}</Tag>}
                      {(row.plusCount ?? 0) > 0 && (
                        <span className={styles.plusBadge}>+{row.plusCount}</span>
                      )}
                      {row.note && <span className={styles.rowNote}>{row.note}</span>}
                    </div>
                    <div className={styles.rowMeta}>
                      <span className={styles.seatCount} aria-label={`${guestSeats(row)} seats`}>
                        {guestSeats(row)}
                      </span>
                      {scanned && (
                        <span className={styles.scannedPill}>
                          <Icon icon={Check} size={16} /> Arrived
                        </span>
                      )}
                      <button
                        type="button"
                        className={styles.remove}
                        aria-label={`Remove ${row.name || "guest"}`}
                        disabled={pending || removeGuest.isPending}
                        onClick={() => row.guestId && removeGuest.mutate(row.guestId)}
                      >
                        <Icon icon={X} size={16} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
